import React from "react";
import { Label } from "@/components/ui/label";

interface FormFieldProps {
  label: React.ReactNode;
  htmlFor: string;
  children: React.ReactNode;
  required?: boolean;
  error?: string;
  className?: string;
}

/**
 * FormField component for consistent form field layout and styling
 * 
 * @param label - Display label for the form field (string or React element)
 * @param htmlFor - ID of the input element this label is associated with
 * @param children - Input element(s) to render inside the form field
 * @param required - Whether this field is required (adds visual indicator)
 * @param error - Error message to display (if any)
 * @param className - Additional CSS classes to apply to the container
 */
export function FormField({
  label,
  htmlFor,
  children,
  required = false,
  error,
  className = "",
}: FormFieldProps) {
  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor={htmlFor} className="text-sm font-medium text-foreground flex items-center">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}
    </div>
  );
}